/**
 * ALDER SEARCH — Footer Component
 * Design: "Alder Forest — Organic Precision"
 * Deep forest green, brass accents, editorial layout
 */

import { Link } from "wouter";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const handleNavClick = (href: string) => {
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      style={{ background: "oklch(0.15 0.05 155)", borderTop: "1px solid oklch(0.245 0.065 155)" }}
    >
      <div className="container py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <svg
                width="28"
                height="32"
                viewBox="0 0 32 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 2C16 2 4 8 4 18C4 24.627 9.373 30 16 30C22.627 30 28 24.627 28 18C28 8 16 2 16 2Z"
                  fill="oklch(0.62 0.085 75)"
                  opacity="0.9"
                />
                <path
                  d="M16 30L16 35"
                  stroke="oklch(0.62 0.085 75)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M16 10L16 26"
                  stroke="oklch(0.15 0.05 155)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                <path
                  d="M16 16L11 13"
                  stroke="oklch(0.15 0.05 155)"
                  strokeWidth="1"
                  strokeLinecap="round"
                  opacity="0.5"
                />
                <path
                  d="M16 20L21 17"
                  stroke="oklch(0.15 0.05 155)"
                  strokeWidth="1"
                  strokeLinecap="round"
                  opacity="0.5"
                />
              </svg>
              <span
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: "oklch(0.92 0.01 80)",
                  letterSpacing: "-0.01em",
                }}
              >
                Alder Search
              </span>
            </div>
            <p
              className="mb-6"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontWeight: 300,
                fontSize: "0.88rem",
                lineHeight: 1.8,
                color: "oklch(0.72 0.02 155)",
                maxWidth: "320px",
              }}
            >
              We recruit product leaders and senior product managers for VC and PE backed companies.
            </p>
            <div className="flex gap-3">
              {["LinkedIn"].map((social) => (
                <button
                  key={social}
                  className="font-ui text-xs px-3 py-1.5 transition-all duration-200"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 500,
                    fontSize: "0.68rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    background: "oklch(0.245 0.065 155)",
                    color: "oklch(0.78 0.02 155)",
                    borderRadius: "2px",
                    border: "none",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = "oklch(0.72 0.09 75)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = "oklch(0.78 0.02 155)";
                  }}
                >
                  {social}
                </button>
              ))}
            </div>
          </div>

          {/* Alder Search Column */}
          <div>
            <h4
              className="mb-1"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 400,
                fontSize: "0.65rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Alder Search
            </h4>
            <div className="flex flex-col gap-2.5 mb-8">
              {[
                { label: "CPO Search", href: "/executive-search/cpo-search" },
                { label: "Product Board Director Placement", href: "/executive-search/board-director-placement" },
                { label: "Product Leadership Team Buildouts", href: "/executive-search/leadership-team-buildouts" },
                { label: "Fractional & Interim Product Leadership", href: "/executive-search/fractional-interim-leadership" },
              ].map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-left"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 400,
                    fontSize: "0.82rem",
                    color: "oklch(0.72 0.02 155)",
                    textDecoration: "none",
                    padding: "2px 0",
                    display: "block",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.015 80)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.02 155)";
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <h4
              className="mb-1"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 400,
                fontSize: "0.65rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Alder Talent
            </h4>
            <div className="flex flex-col gap-2.5">
              {[
                { label: "Head of Product Search", href: "/senior-leadership/head-of-product-search" },
                { label: "VP Product & Senior IC Search", href: "/senior-leadership/vp-specialist-search" },
                { label: "Senior IC & Staff Product Search", href: "/senior-leadership" },
              ].map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-left"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontWeight: 400,
                    fontSize: "0.82rem",
                    color: "oklch(0.72 0.02 155)",
                    textDecoration: "none",
                    padding: "2px 0",
                    display: "block",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.015 80)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.02 155)";
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Company Column */}
          <div>
            <h4
              className="mb-4"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontWeight: 400,
                fontSize: "0.65rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Company
            </h4>
            <div className="flex flex-col gap-2.5">
              {[
                { label: "Our Approach", href: "/approach", external: true },
                { label: "Sector Coverage", href: "#sectors" },
                { label: "Leadership", href: "#team" },
                { label: "Insights", href: "/insights", external: true },
                { label: "Contact", href: "#contact" },
                { label: "Privacy Policy", href: "/privacy", external: true },
              ].map((item) =>
                item.external ? (
                  <Link
                    key={item.label}
                    href={item.href}
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      fontSize: "0.82rem",
                      color: "oklch(0.72 0.02 155)",
                      textDecoration: "none",
                      padding: "2px 0",
                      display: "block",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.015 80)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.02 155)";
                    }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item.href)}
                    className="text-left"
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      fontSize: "0.82rem",
                      color: "oklch(0.72 0.02 155)",
                      background: "none",
                      border: "none",
                      padding: "2px 0",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = "oklch(0.88 0.015 80)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = "oklch(0.72 0.02 155)";
                    }}
                  >
                    {item.label}
                  </button>
                )
              )}            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8"
          style={{ borderTop: "1px solid oklch(0.245 0.065 155)" }}
        >
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontWeight: 300,
              fontSize: "0.65rem",
              color: "oklch(0.68 0.015 155)",
              letterSpacing: "0.06em",
            }}
          >
            © {currentYear} Alder Search Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
